import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterService, GridDataResult, KENDO_GRID, PageChangeEvent, PagerSettings } from '@progress/kendo-angular-grid';
import { ParkingLotResponseModel } from './models/parking-lot-response-model';
import { ParkingLotService } from './parking-lot.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ParkingLotViewComponent } from './parking-lot-view.component';
import { ViewItemComponent } from '../items/child-components/view-item.component';
import { ItemSearchResponseModel } from '../items/models/item-search-response-model';
import { Observable } from 'rxjs';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';

@Component({
  selector: 'parking-lot',
  standalone: true,
  imports: [CommonModule, FormsModule, KENDO_GRID],
  providers: [ParkingLotService],
  templateUrl: './parking-lot.component.html',
  styleUrls: ['./parking-lot.component.css']
})
export class ParkingLotComponent implements OnInit {
  public view: Observable<GridDataResult>;

  public parkedReasonDropdownOptions = [
    { value: '10', text: 'ReplacedBy item not yet received' },
    { value: '20', text: 'ReplacedBy item has no suppliers' },
    { value: '30', text: 'ReplacedBy item has no Active supplier' },
    { value: '40', text: 'Country of Origin not set' },
    { value: '50', text: 'ALSIS Product Code not set' },
    { value: '60', text: 'Net Weight not set' },
    { value: '70', text: 'The item which is referenced does not exist' },
    { value: '80', text: 'Cannot replace already replaced item' }
  ];

  public state: State = {
    skip: 0,
    take: 20,
  };

  public pageableSettings: PagerSettings = {
    pageSizes: [10, 20, 50, 100]
  };

  public filter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [{ field: 'isParked', operator: 'eq', value: true }],
  };

  constructor(public parkingLotService: ParkingLotService, private modalService: NgbModal) {
    this.view = this.parkingLotService;
    this.parkingLotService.query(this.state, this.filter);
  }

  ngOnInit(): void {}

  openViewModal(parkingLotModel: ParkingLotResponseModel) {
    const modalRef = this.modalService.open(ParkingLotViewComponent, { size: 'size_60' });
    modalRef.componentInstance.parkingLotModel = parkingLotModel;
  }

  public canViewItem(dataItem: ParkingLotResponseModel): boolean {
    const excludedCodes = [10, 70];
    return !!dataItem?.itemId && !excludedCodes.includes(Number(dataItem.parkedReasonCode));
  }

  public viewItem(itemId: string): void {
    if (!itemId) return;
    const modalRef = this.modalService.open(ViewItemComponent, { size: 'size_60' });
    modalRef.componentInstance.itemModel = { itemId } as ItemSearchResponseModel;
  }

  public pageChange(state: PageChangeEvent): void {
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.parkingLotService.query(this.state, this.filter);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.state.skip = 0;
    this.parkingLotService.query(this.state, this.filter);
  }

  public refreshData(): void {
    this.parkingLotService.query(this.state, this.filter);
  }

  public getFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    return descriptor ? String(descriptor.value ?? '') : '';
  }

  public onIsParkedFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor,
    field: string
  ): void {
    const updatedFilter: CompositeFilterDescriptor = {
      logic: this.filter?.logic ?? 'and',
      filters: this.removeFieldFilter(this.filter?.filters ?? [], field)
    };
    if (value !== '') {
      updatedFilter.filters.push({ field, operator: 'eq', value: value === 'true' });
    }
    this.filterChange(updatedFilter);
  }

  public onParkedReasonFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor
  ): void {
    const field = 'parkedReasonCode';
    const updatedFilter: CompositeFilterDescriptor = {
      logic: this.filter?.logic ?? 'and',
      filters: this.removeFieldFilter(this.filter?.filters ?? [], field)
    };
    if (value !== '') {
      updatedFilter.filters.push({ field, operator: 'eq', value: Number(value) });
    }
    this.filterChange(updatedFilter);
  }

  private findFieldFilter(filter: CompositeFilterDescriptor, field: string): FilterDescriptor | null {
    for (const item of filter.filters) {
      if ('filters' in item) {
        const nested = this.findFieldFilter(item, field);
        if (nested) return nested;
      } else if (item.field === field) {
        return item;
      }
    }
    return null;
  }

  private removeFieldFilter(
    filters: Array<FilterDescriptor | CompositeFilterDescriptor>,
    field: string
  ): Array<FilterDescriptor | CompositeFilterDescriptor> {
    return filters.reduce((acc, item) => {
      if ('filters' in item) {
        const nested = this.removeFieldFilter(item.filters, field);
        if (nested.length) acc.push({ logic: item.logic, filters: nested });
      } else if (item.field !== field) {
        acc.push(item);
      }
      return acc;
    }, [] as Array<FilterDescriptor | CompositeFilterDescriptor>);
  }
}
